import { useCallback, useEffect, useMemo, useState } from "react";
import { refundNote } from "../utils/salePayments";
import { api } from "../api/apiClient";
import type {
  AnalyticsSalesSummary,
  AnalyticsSpendSummary,
  AnalyticsStockValue,
  AnalyticsWasteSummary,
  ItemCategory,
  Location,
  PayrollSummaryResponse,
} from "../api/types";
import { AnalyticsPieChart } from "../components/analytics/AnalyticsPieChart";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Select } from "../components/ui/Select";
import { StatTile } from "../components/ui/StatTile";
import { useAuth } from "../state/AuthContext";
import { businessDaysAgo, businessToday } from "../utils/businessDay";
import { PayrollTotalNote, PayrollWageCell, countMissingRates } from "../components/finances/PayrollWageCell";
import { CostNote } from "../components/ui/CostNote";
import {
  costOrUnknown,
  hasUnknown,
  joinNotes,
  partlyKnownCost,
  salesWithUnknownCostNote,
  unknownEntriesNotCountedNote,
  unknownLotsNotCountedNote,
  unpricedReceivesNote,
} from "../utils/unknownCost";
import { wasteCauseLabel } from "../utils/wasteCause";

type RangePreset =
  | "today"
  | "7d"
  | "30d"
  | "month"
  | "year"
  | "all"
  | "custom";

const PRESET_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom" },
];

const eur = (v: string | number | null | undefined) => {
  const n = v == null ? NaN : Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, {
        style: "currency",
        currency: "EUR",
      })
    : "—";
};

/** Entity-stable pie slots: a cause keeps its color across every period. */
const WASTE_CAUSE_SLOTS: Record<string, number> = {
  WILTED_ON_FLOOR: 1,
  BROKEN_DURING_PREP: 2,
  CUSTOMER_RETURN: 3,
  RECEIVING_DAMAGE: 4,
  OTHER: 5,
  STAFF_ACCIDENT: 6,
};

/**
 * One line of the G-29 margin chain. The sign is shown explicitly (+ / −)
 * because the owner reads this as a running sum, not as a table of unrelated
 * figures — every value is stored positive, the sign says which way it moves.
 */
function ChainRow({
  label,
  value,
  sign,
  note,
}: {
  label: string;
  value: string;
  sign: "plus" | "minus";
  note?: string;
}) {
  return (
    <div className="an-chain-row">
      <dt>
        {label}
        {note ? <span className="muted an-chain-note">{note}</span> : null}
      </dt>
      <dd>
        <span className="an-chain-sign">{sign === "plus" ? "+" : "−"}</span>
        {value}
      </dd>
    </div>
  );
}

/**
 * G-175 (KI-53): the period as whole Barcelona days "YYYY-MM-DD" — the days
 * Sales records, payroll and Home count (the server turns each into its
 * Madrid midnights, `business-day.ts`). No bound = all time. "7 days" and
 * "30 days" are that many calendar days counting today; this month and this
 * year run from their Barcelona 1st to today.
 */
function presetRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  const today = businessToday();
  switch (preset) {
    case "today":
      return { from: today.ymd, to: today.ymd };
    case "7d":
      return { from: businessDaysAgo(6), to: today.ymd };
    case "30d":
      return { from: businessDaysAgo(29), to: today.ymd };
    case "month":
      return { from: `${today.ymd.slice(0, 7)}-01`, to: today.ymd };
    case "year":
      return { from: `${today.year}-01-01`, to: today.ymd };
    case "all":
      return {};
    case "custom":
      return {
        ...(customFrom ? { from: customFrom } : {}),
        ...(customTo ? { to: customTo } : {}),
      };
  }
}

/**
 * Analytics (Finalizing.MD T4+T5): per shop and time range — what the
 * shop generated (sales, per person) vs what it spent (stock purchases by
 * category/supplier, staff wage+commission, waste losses), plus the current
 * value of everything sitting on the shelves. Migrated to tokens/kit in
 * G-9.3 item 2 — zero inline styles, zero raw native controls.
 *
 * G-57 (owner, 2026-09-09): the OWNER sees the whole business ("All
 * shops", the default) or any one shop; a MANAGER sees everything about
 * their own shop and nothing else. The server decides the scope from the
 * signed-in person — the shop picker here is only the ask.
 */
export function AnalyticsPage() {
  const { user, hasRole } = useAuth();
  const isOwner = hasRole("OWNER");
  // "" = every shop (owner only). A manager's id is fixed to their assignment.
  const managerLocation = !isOwner ? (user?.assignedLocation ?? null) : null;
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState(managerLocation?.id ?? "");
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [customFrom, setCustomFrom] = useState(() => businessDaysAgo(29));
  const [customTo, setCustomTo] = useState(() => businessToday().ymd);
  const [spendCategoryId, setSpendCategoryId] = useState("");
  const [stockCategoryId, setStockCategoryId] = useState("");

  const [sales, setSales] = useState<AnalyticsSalesSummary | null>(null);
  const [spend, setSpend] = useState<AnalyticsSpendSummary | null>(null);
  const [waste, setWaste] = useState<AnalyticsWasteSummary | null>(null);
  const [stock, setStock] = useState<AnalyticsStockValue | null>(null);
  const [payroll, setPayroll] = useState<PayrollSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOwner) {
      // Owners pick; the default is every shop, so the picker starts empty.
      void api
        .listLocations()
        .then((rows) => setLocations(rows.filter((l) => l.isActive)))
        .catch(() => setLocations([]));
    }
    void api
      .listCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [isOwner]);

  const range = useMemo(
    () => presetRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // Payroll's range mode is capped at ~1 year, so staff cost is hidden for
  // "all time" instead of silently showing a truncated number.
  const staffCostAvailable = preset !== "all";

  const refresh = useCallback(async () => {
    // A manager with no assigned shop has nothing to look at; the server
    // would refuse too. Owners may leave the shop empty (= all shops).
    if (!isOwner && !locationId) {
      setError("You need an assigned shop to see analytics. Ask the owner to assign you.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const shop = locationId ? { locationId } : {};
      const base = { ...shop, ...range };
      const [salesRes, spendRes, wasteRes, stockRes] = await Promise.all([
        api.getAnalyticsSales(base),
        api.getAnalyticsSpend({
          ...base,
          ...(spendCategoryId ? { categoryId: spendCategoryId } : {}),
        }),
        api.getAnalyticsWaste(base),
        api.getAnalyticsStockValue({
          ...shop,
          ...(stockCategoryId ? { categoryId: stockCategoryId } : {}),
        }),
      ]);
      setSales(salesRes);
      setSpend(spendRes);
      setWaste(wasteRes);
      setStock(stockRes);

      if (staffCostAvailable && range.from && range.to) {
        // G-126 ([119]) / G-175 (KI-53): payroll takes whole Barcelona days —
        // the very days the sales, spend and waste above were asked for.
        // Staff cost is "what this period cost": bonuses and deductions by the
        // day they happened, not by booked month (G-126 review, owner 2026-10-04).
        setPayroll(
          await api.getPayrollSummary({
            from: range.from,
            to: range.to,
            adjustmentsBy: "occurred",
            ...shop,
          }),
        );
      } else {
        setPayroll(null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [isOwner, locationId, range, spendCategoryId, stockCategoryId, staffCostAvailable]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // G-108 (OD-79): pay is owner-only, so a manager's payroll arrives as hours
  // alone and there is no staff cost. G-169 (KI-116): the server's figure,
  // without the commission the chain below already carries — so "Margin"
  // less "Staff cost" counts it once.
  const staffCostEur = payroll?.payVisible ? payroll.totals.staffCostExcludingCommissionEur : null;
  // WO-B (OD-198): people whose hours count €0 for want of a rate — the tile says so.
  const missingRateCount = payroll?.payVisible ? countMissingRates(payroll.rows) : 0;
  // WO-B parts 5–6 (OD-198): the figures leave stock of unknown cost out and say so.
  const unknownCostSalesNote = salesWithUnknownCostNote(sales?.salesWithUnknownCostCount);
  const wasteUnknownNote = unknownEntriesNotCountedNote(waste?.unknownCostEntryCount);
  const stockUnknownNote = unknownLotsNotCountedNote(stock?.unknownCostBatchCount);
  // G-175 (KI-153): receives with no price add €0 to the spend — say how many.
  const spendUnpricedNote = unpricedReceivesNote(spend?.unpricedReceiveCount);

  const wasteChartData = useMemo(
    () =>
      (waste?.byCause ?? []).map((row) => ({
        key: row.cause,
        label: wasteCauseLabel(row.cause),
        value: Number(row.costEur),
      })),
    [waste],
  );

  const salesChartData = useMemo(
    () =>
      (sales?.bySeller ?? [])
        // G-162: a seller whose period holds only refunds nets below zero —
        // a share of a pie cannot be negative.
        .filter((row) => Number(row.totalEur) > 0)
        .map((row) => ({
          key: row.userId,
          label: row.fullName,
          value: Number(row.totalEur),
        })),
    [sales],
  );

  // G-29: the headline is now the CONTRIBUTION — goods margin less
  // commission, plus the delivery leg (delivery revenue in, courier payout
  // out) that DeliveryJob has carried since Phase 3 and nothing ever showed.
  const contribution = Number(sales?.contributionEur ?? NaN);
  const negative = Number.isFinite(contribution) && contribution < 0;
  // G-30: the same chain with IVA stripped out of goods prices. The server
  // computes it (`vat.contributionAfterVatEur`) so the arithmetic lives in
  // one place instead of being re-derived per screen.
  const afterVat = Number(sales?.vat.contributionAfterVatEur ?? NaN);
  const afterVatNegative = Number.isFinite(afterVat) && afterVat < 0;

  return (
    <section className="page-stack">
      <div>
        <h1 className="page-title">Analytics</h1>
        <p className="page-subtitle">
          Sales performance, staff costs, inventory purchases, waste, and the
          current value of stock on hand.
        </p>
      </div>

      {/* Filters */}
      <div className="card an-filters">
        <div className="field">
          <label htmlFor="an-shop">Shop</label>
          {isOwner ? (
            <Select
              id="an-shop"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">All shops</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.code})
                </option>
              ))}
            </Select>
          ) : (
            <p id="an-shop" className="muted an-note">
              {managerLocation
                ? `${managerLocation.name} (${managerLocation.code}) — your shop`
                : "No shop assigned"}
            </p>
          )}
        </div>
        <div className="field">
          <label id="an-range-label">Period</label>
          <SegmentedControl
            ariaLabel="Period"
            value={preset}
            onChange={setPreset}
            options={PRESET_OPTIONS}
          />
        </div>
        {preset === "custom" ? (
          <>
            <div className="field">
              <label htmlFor="an-from">From</label>
              <input
                id="an-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="an-to">To</label>
              <input
                id="an-to"
                type="date"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </>
        ) : null}
        {loading ? <span className="muted">Loading…</span> : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {/* Summary tiles — color only where it means something (§G-9.3 item 2:
          a negative profit must read as a loss). The headline tile is the
          figure compared month to month and is kept with the goods IVA inside
          (§6 G-30 — "headline figures stay gross so history stays
          comparable"); its label says so (G-168, the money words). The IVA
          row and the figure without it are in the chain directly below. */}
      <div className="stat-tile-row">
        <StatTile
          label="Revenue"
          value={eur(sales?.totalEur)}
          // G-162: refunds given in the period are already taken off.
          hint={sales ? (refundNote(Number(sales.refunds.totalEur), (n) => eur(n)) ?? undefined) : undefined}
          tone="positive"
        />
        <StatTile
          label="Profit before running costs, with IVA"
          value={eur(sales?.contributionEur)}
          hint={
            joinNotes(
              "After cost of goods, courier payouts & commission",
              unknownCostSalesNote,
            ) ?? undefined
          }
          tone={negative ? "negative" : "neutral"}
        />
        <StatTile
          label="Inventory purchased"
          value={eur(spend?.totalEur)}
          hint={joinNotes("Received in the period · before IVA", spendUnpricedNote) ?? undefined}
        />
        {isOwner ? (
          <StatTile
            label="Staff cost"
            value={staffCostAvailable ? eur(staffCostEur) : "—"}
            hint={
              !staffCostAvailable
                ? "Pick a bounded period"
                : missingRateCount > 0
                  ? `Wages, salaries, bonuses − deductions · ${missingRateCount} with no hourly rate — understated`
                  : "Wages, salaries, bonuses − deductions"
            }
          />
        ) : null}
        <StatTile
          label="Waste losses"
          value={eur(waste?.totalCostEur)}
          tone="warning"
          hint={joinNotes("Before IVA", wasteUnknownNote) ?? undefined}
        />
      </div>

      {/* G-29: the profit as a chain rather than one number, so a
          non-technical owner can see every euro that goes in and out —
          including the delivery leg, which the app has been computing per
          job since Phase 3 and had never once displayed. Row names are the
          glossary's (G-168). */}
      <div className="card an-card">
        <div className="an-card-head">
          <strong>How the profit adds up</strong>
          <span className="muted">Before running costs</span>
        </div>
        <dl className="an-chain">
          <ChainRow
            label="Revenue"
            value={eur(sales?.totalEur)}
            sign="plus"
            note="with IVA"
          />
          <ChainRow
            label="Cost of goods"
            value={eur(sales?.totalCogsEur)}
            sign="minus"
            note={joinNotes("before IVA", unknownCostSalesNote) ?? undefined}
          />
          <ChainRow
            label="Delivery fees charged"
            value={eur(sales?.delivery.shippingChargedEur)}
            sign="plus"
            note={
              joinNotes(
                sales && sales.delivery.jobCount > 0
                  ? `${sales.delivery.jobCount} delivered`
                  : null,
                sales && Number(sales.delivery.uncollectedShippingEur) > 0
                  ? `${eur(sales.delivery.uncollectedShippingEur)} not yet collected`
                  : null,
              ) ?? undefined
            }
          />
          {sales && Number(sales.delivery.shippingRefundedEur) > 0 ? (
            <ChainRow
              label="Delivery fees refunded in Shopify"
              value={eur(sales.delivery.shippingRefundedEur)}
              sign="minus"
            />
          ) : null}
          <ChainRow
            label="Courier payouts"
            value={eur(sales?.delivery.courierCostEur)}
            sign="minus"
            note={
              sales && sales.delivery.jobsMissingPayoutCount > 0
                ? `${sales.delivery.jobsMissingPayoutCount} without a payout rule — understated`
                : undefined
            }
          />
          <ChainRow
            label="Commission"
            value={eur(sales?.totalCommissionEur)}
            sign="minus"
          />
          <div className="an-chain-row is-total">
            <dt>Profit before running costs, with IVA</dt>
            <dd className={negative ? "tone-negative" : undefined}>
              {eur(sales?.contributionEur)}
            </dd>
          </div>
          {/* G-30: the IVA taken out of the goods prices via the per-line
              sale-time snapshots. Delivery money stays with its IVA inside —
              its treatment is not modelled yet. */}
          <ChainRow
            label="Goods IVA"
            value={eur(sales?.vat.vatEur)}
            sign="minus"
          />
          <div className="an-chain-row is-total">
            <dt>
              Profit before running costs
              <span className="muted an-chain-note">delivery money with IVA</span>
            </dt>
            <dd className={afterVatNegative ? "tone-negative" : undefined}>
              {eur(sales?.vat.contributionAfterVatEur)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="analytics-pie-grid">
        <AnalyticsPieChart
          title="Waste losses by cause"
          description="Share of the selected period's waste cost."
          data={wasteChartData}
          slotByKey={WASTE_CAUSE_SLOTS}
          emptyMessage="No costed waste was logged in this period."
        />
        <AnalyticsPieChart
          title="Revenue by person"
          description="Share of revenue by the person who logged each sale."
          data={salesChartData}
          emptyMessage="No sales were logged in this period."
        />
      </div>

      {/* Sales by person */}
      <div className="card an-card">
        <strong>Sales by person — {sales?.saleCount ?? 0} sales</strong>
        {sales && sales.bySeller.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Sales</th>
                <th>Revenue</th>
                <th>Commission</th>
              </tr>
            </thead>
            <tbody>
              {sales.bySeller.map((r) => (
                <tr key={r.userId}>
                  <td>
                    {r.fullName} <span className="muted">{r.role}</span>
                  </td>
                  <td>{r.saleCount}</td>
                  <td>{eur(r.totalEur)}</td>
                  <td>{eur(r.commissionEur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted an-note">No sales in this period.</p>
        )}
      </div>

      {/* Staff cost — or, for a manager (G-108 / OD-79), hours alone. */}
      <div className="card an-card">
        <strong>{isOwner ? "Staff cost" : "Hours worked"}</strong>
        {!staffCostAvailable ? (
          <p className="muted an-note">
            {isOwner ? "Staff cost" : "Hours"} needs a bounded period (up to one year) — pick any
            period other than all time.
          </p>
        ) : payroll && !payroll.payVisible && payroll.rows.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {payroll.rows.map((r) => (
                <tr key={r.userId}>
                  <td>
                    {r.fullName} <span className="muted">{r.role}</span>
                  </td>
                  <td>{r.hoursWorked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : payroll?.payVisible && payroll.rows.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Hours</th>
                <th>Wage</th>
                <th>Commission</th>
                <th>Bonus</th>
                <th>Deduction</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {payroll.rows.map((r) => (
                <tr key={r.userId}>
                  <td>
                    {r.fullName} <span className="muted">{r.role}</span>
                  </td>
                  <td>{r.hoursWorked}</td>
                  <td>
                    <PayrollWageCell row={r} format={eur} />
                  </td>
                  <td>{eur(r.commissionOwedEur)}</td>
                  <td>{eur(r.bonusEur)}</td>
                  <td>{eur(r.deductionEur)}</td>
                  <td>
                    {/* OD-285: what the person cost in the period (salary as a
                        share per calendar day) — the tile's figure plus the
                        commission column; Finances keeps what is owed. */}
                    <strong>{eur(r.periodCostEur)}</strong>
                    <PayrollTotalNote row={r} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted an-note">No staff activity in this period.</p>
        )}
      </div>

      {/* Product spend */}
      <div className="card an-card">
        <div className="an-card-head">
          <strong>
            Product spend — {eur(spend?.totalEur)} across {spend?.batchCount ?? 0} deliveries
            <CostNote note={spendUnpricedNote} />
          </strong>
          <Select
            aria-label="Spend category filter"
            value={spendCategoryId}
            onChange={(e) => setSpendCategoryId(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="an-split">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Spend</th>
                <th>Deliveries</th>
              </tr>
            </thead>
            <tbody>
              {(spend?.byCategory ?? []).map((r) => (
                <tr key={r.categoryId}>
                  <td>{r.label}</td>
                  <td>
                    {eur(r.totalEur)}
                    <CostNote note={unpricedReceivesNote(r.unpricedCount)} />
                  </td>
                  <td className="muted">{r.batchCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Spend</th>
                <th>Deliveries</th>
              </tr>
            </thead>
            <tbody>
              {(spend?.bySupplier ?? []).map((r) => (
                <tr key={r.supplierId ?? "none"}>
                  <td>{r.name}</td>
                  <td>
                    {eur(r.totalEur)}
                    <CostNote note={unpricedReceivesNote(r.unpricedCount)} />
                  </td>
                  <td className="muted">{r.batchCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock value now */}
      <div className="card an-card">
        <div className="an-card-head">
          <strong>
            Stock on shelves right now — {eur(stock?.totalEur)} ({stock?.itemCount ?? 0} items)
            <CostNote note={stockUnknownNote} />
          </strong>
          <Select
            aria-label="Stock category filter"
            value={stockCategoryId}
            onChange={(e) => setStockCategoryId(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
        </div>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Paid for stock on hand</th>
              <th>Items</th>
            </tr>
          </thead>
          <tbody>
            {(stock?.byCategory ?? []).map((r) => (
              <tr key={r.categoryId}>
                <td>{r.label}</td>
                <td>{partlyKnownCost(r.valueEur, hasUnknown(r.unknownCostQty), eur)}</td>
                <td className="muted">{r.itemCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Waste */}
      <div className="card an-card">
        <strong>
          Waste — {eur(waste?.totalCostEur)} lost over {waste?.entryCount ?? 0} entries
          <CostNote note={wasteUnknownNote} />
        </strong>
        <div className="an-split">
          <table>
            <thead>
              <tr>
                <th>Cause</th>
                <th>Qty</th>
                <th>Loss</th>
              </tr>
            </thead>
            <tbody>
              {(waste?.byCause ?? []).map((r) => (
                <tr key={r.cause}>
                  <td>{wasteCauseLabel(r.cause)}</td>
                  <td className="muted">{r.qty}</td>
                  <td>{partlyKnownCost(r.costEur, hasUnknown(r.unknownCostQty), eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table>
            <thead>
              <tr>
                <th>Logged by</th>
                <th>Entries</th>
                <th>Loss</th>
              </tr>
            </thead>
            <tbody>
              {(waste?.byActor ?? []).map((r) => (
                <tr key={r.userId}>
                  <td>{r.fullName}</td>
                  <td className="muted">{r.count}</td>
                  <td>{partlyKnownCost(r.costEur, hasUnknown(r.unknownCostQty), eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {waste && waste.recent.length > 0 ? (
          <details>
            <summary className="muted">Recent waste entries ({waste.recent.length})</summary>
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Cause</th>
                  <th>By</th>
                  <th>Loss</th>
                </tr>
              </thead>
              <tbody>
                {waste.recent.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">
                      {new Date(r.occurredAt).toLocaleDateString()}
                    </td>
                    <td>{r.itemName}</td>
                    <td>
                      {r.quantity} {r.uom}
                    </td>
                    <td className="muted">{wasteCauseLabel(r.cause)}</td>
                    <td>{r.loggedBy}</td>
                    <td>{costOrUnknown(r.costEur, eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : null}
      </div>
    </section>
  );
}
